<template>
  <div
    class="hubspot-card"
    :class="{
      'hubspot-card--collapsed': heroState === 'empty',
      'hubspot-card--revealed': heroState === 'visible',
    }"
    :style="cardStyle"
  >
    <div ref="cardContent" class="hubspot-card__content">
      <div
        ref="hubspotContainer"
        class="hubspot-card__embed"
        :class="[`hs-cta-embed hs-cta-simple-placeholder hs-cta-embed-${hubspotId}`]"
        :data-hubspot-wrapper-cta-id="hubspotId"
        :aria-busy="isLoading"
      ></div>
    </div>

    <transition name="fade">
      <div
        v-if="heroState === 'loading' || heroState === 'retrying'"
        class="hubspot-card__overlay"
        data-test="hubspot-card-loader"
      >
        <ActionLoader :size="1.4" loaderClass="hubspot-card__spinner" />
      </div>
    </transition>

    <div v-if="heroState === 'error'" class="hubspot-card__error" data-test="hubspot-card-error">
      <p class="hubspot-card__error-text">
        {{ errorMessage || 'Unable to load this content right now.' }}
      </p>
      <button type="button" class="hubspot-card__retry" @click="reload">Retry</button>
    </div>
  </div>
</template>

<script>
import { ref, watch, onBeforeUnmount, computed } from 'vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { useHubspotEmbed } from '@/composables/useHubspotEmbed'

const HERO_FIXED_HEIGHT_PX = 600
const EMPTY_RESOLUTION_MS = 800

const parsePxValue = value => {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number.parseFloat(String(value).replace('px', '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

const findRenderableNode = container => {
  if (!container) return null

  const candidates = [
    container.querySelector('iframe'),
    container.querySelector('.hs-cta-wrapper'),
    container.querySelector('.hs-cta-node'),
    container.querySelector('.hs_cos_wrapper'),
    container.firstElementChild,
  ]

  return candidates.find(Boolean) || null
}

export default {
  name: 'HubspotCard',
  emits: ['state-change', 'visibility-change', 'visibility-resolved'],
  props: {
    hubspotId: {
      type: String,
      required: true,
    },
    imgSrc: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const hubspotContainer = ref(null)
    const cardContent = ref(null)
    const bannerHeight = ref(null)
    const heroState = ref('loading')

    const { status, isLoading, hasError, errorMessage, reload } = useHubspotEmbed(
      hubspotContainer,
      {
        maxRetries: 3,
        timeout: 1800,
        retryDelay: 150,
        enablePostLoadMonitor: false,
      }
    )

    let mutationObserver = null
    let resizeObserver = null
    let emptyResolutionTimer = null

    const clearEmptyResolutionTimer = () => {
      if (emptyResolutionTimer) {
        clearTimeout(emptyResolutionTimer)
        emptyResolutionTimer = null
      }
    }

    const emitVisibilityState = state => {
      emit('state-change', state)
      if (state === 'loading' || state === 'retrying') return
      const isVisible = state === 'visible'
      emit('visibility-change', isVisible)
      emit('visibility-resolved', isVisible)
    }

    const isNodeActuallyVisible = node => {
      if (!node) return false
      const computedStyles = window.getComputedStyle(node)
      const rect = node.getBoundingClientRect()
      const hasDimensions = rect.width > 0 && rect.height > 0
      const opacity = Number.parseFloat(computedStyles.opacity)
      return (
        computedStyles.display !== 'none' &&
        computedStyles.visibility !== 'hidden' &&
        opacity > 0 &&
        hasDimensions
      )
    }

    const syncVisibility = () => {
      const container = hubspotContainer.value
      const renderNode = findRenderableNode(container)
      if (!renderNode) return false

      const rect = renderNode.getBoundingClientRect()
      const computedStyles = window.getComputedStyle(renderNode)
      const nodeHeight =
        parsePxValue(renderNode.getAttribute('height')) ||
        parsePxValue(renderNode.style.height) ||
        parsePxValue(computedStyles.height) ||
        (rect.height >= 0 ? Math.round(rect.height) : null)
      const containerHeight =
        parsePxValue(container?.getAttribute('height')) ||
        parsePxValue(container?.style?.height) ||
        parsePxValue(container ? window.getComputedStyle(container).height : null)

      const height = Math.max(nodeHeight || 0, containerHeight || 0) || null
      const isVisible = isNodeActuallyVisible(renderNode)

      if (typeof height === 'number' && height > 0 && isVisible) {
        clearEmptyResolutionTimer()
        bannerHeight.value = height
        if (heroState.value !== 'visible') {
          heroState.value = 'visible'
        }
        return true
      }

      return false
    }

    const initializeObservers = () => {
      if (!hubspotContainer.value) return

      mutationObserver?.disconnect()
      resizeObserver?.disconnect()

      mutationObserver = new MutationObserver(syncVisibility)
      mutationObserver.observe(hubspotContainer.value, {
        childList: true,
        subtree: true,
        attributes: true,
      })

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncVisibility)
        resizeObserver.observe(hubspotContainer.value)
      } else {
        resizeObserver = null
      }

      syncVisibility()
    }

    watch(
      () => [props.hubspotId, props.imgSrc],
      () => {
        bannerHeight.value = null
        heroState.value = 'loading'
        reload()
      }
    )

    watch(hubspotContainer, () => {
      initializeObservers()
    })

    watch(
      () => status.value,
      value => {
        if (value === 'ready') {
          const visibleNow = syncVisibility()
          if (!visibleNow) {
            heroState.value = 'loading'
            clearEmptyResolutionTimer()
            emptyResolutionTimer = setTimeout(() => {
              const visibleAfterGrace = syncVisibility()
              if (!visibleAfterGrace) {
                heroState.value = 'empty'
              }
            }, EMPTY_RESOLUTION_MS)
          }
          return
        }

        if (value === 'loading' || value === 'retrying') {
          clearEmptyResolutionTimer()
          heroState.value = value
          return
        }

        if (value === 'empty') {
          clearEmptyResolutionTimer()
          if (heroState.value !== 'visible') {
            heroState.value = 'empty'
          }
          return
        }

        if (value === 'error' || hasError.value) {
          clearEmptyResolutionTimer()
          heroState.value = 'error'
        }
      },
      { immediate: true }
    )

    watch(
      () => heroState.value,
      state => {
        emitVisibilityState(state)
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      mutationObserver?.disconnect()
      resizeObserver?.disconnect()
      clearEmptyResolutionTimer()
    })

    const cardStyle = computed(() => {
      if (
        heroState.value === 'empty' ||
        heroState.value === 'loading' ||
        heroState.value === 'retrying'
      ) {
        return {
          minHeight: '60px',
          maxHeight: '60px',
        }
      }

      if (heroState.value === 'error') {
        return {
          minHeight: `${HERO_FIXED_HEIGHT_PX}px`,
        }
      }

      if (heroState.value !== 'visible') {
        return {
          minHeight: '60px',
          maxHeight: '60px',
        }
      }

      const contentHeight = cardContent.value?.scrollHeight || 0
      const revealHeight = Math.max(bannerHeight.value || 0, contentHeight, HERO_FIXED_HEIGHT_PX)
      return {
        minHeight: `${revealHeight}px`,
      }
    })

    return {
      hubspotContainer,
      cardContent,
      heroState,
      isLoading,
      errorMessage,
      reload,
      cardStyle,
    }
  },
}
</script>

<style lang="scss" scoped>
.hubspot-card {
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: translateY(0);
  overflow: hidden;
  transition: opacity 0.35s ease;

  &--revealed {
    opacity: 1;
  }

  &--collapsed {
    opacity: 0;
    pointer-events: none;
  }

  &__content {
    display: grid;
    width: 100%;

    > * {
      grid-area: 1 / 1;
    }
  }

  &__embed {
    width: 100%;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.88);
    text-align: center;
    z-index: 2;
  }

  &__error {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #f5c6cb;
    border-radius: 4px;
  }

  &__error-text {
    color: #8b1a1a;
    margin-bottom: 0.75rem;
  }

  &__retry {
    border: none;
    background: #ef7d22;
    color: #fff;
    padding: 0.5rem 1.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
